<template>
	<Modal
		v-model:visible="visible"
		:title="modalTitle"
		:destroyOnClose="true"
		:closable="!isSubmitting"
		:keyboard="!isSubmitting"
		:maskClosable="!isSubmitting"
		width="650px"
		@ok="onOk"
		@cancel="onCancel"
	>
		<template v-if="item != null">
			<Form
				:model="formState"
				:label-col="{ span: 12 }"
				:wrapper-col="{ span: 12 }"
				:colon="false"
			>
				<FormItem
					:label="$t('settings.vrsSettingsForm.ftpSettings.protocol')"
					v-bind="validateInfos.userProtocol"
				>
					<RadioGroup v-model:value="formState.userProtocol" :options="protocols" option-type="button" />
				</FormItem>
				<FormItem
					:label="$t('settings.vrsSettingsForm.ftpSettings.host')"
					v-bind="validateInfos.host"
				>
					<InputRestricted
						v-model="formState.host"
						:allowedFormatChecker="hostnameChecker"
						:toolTipText="$t('validators.hostnameMessage')"
						showTooltipOnFocus
					/>
				</FormItem>
				<FormItem
					:label="$t('settings.vrsSettingsForm.ftpSettings.port')"
					v-bind="validateInfos.port"
				>
					<InputDecimal v-model="formState.port" :allowPoint="false" range="port" />
				</FormItem>
				<FormItem
					:label="$t('settings.vrsSettingsForm.ftpSettings.username')"
					v-bind="validateInfos.username"
				>
					<InputRestricted
						v-model="formState.username"
						:allowedFormatChecker="alphaNumericChecker"
						:toolTipText="$t('validators.alphaNumericMessage')"
					/>
				</FormItem>
				<FormItem
					:label="$t('settings.vrsSettingsForm.ftpSettings.password')"
					v-bind="validateInfos.password"
				>
					<InputRestricted
						v-model="formState.password"
						:allowedFormatChecker="alphaNumericChecker"
						:toolTipText="$t('validators.alphaNumericMessage')"
					/>
				</FormItem>

				<FormItem
					:label="$t('settings.vrsSettingsForm.ftpSettings.' + item.key)"
					v-bind="validateInfos.fullFilename"
				>
					<InputRestricted
						v-model="formState.fullFilename"
						:allowedFormatChecker="filenameChecker"
						:toolTipText="$t('validators.filenameMessage')"
						showTooltipOnFocus
					/>
				</FormItem>
			</Form>
			<div v-if="item.defaultValue != null">
				<DividerAutoDirection>{{ $t('settings.vrsSettingsForm.ftpDefaultsDivider') }}</DividerAutoDirection>
				<Descriptions size="small" :column="1" :bordered="true" :colon="false">
					<DescriptionsItem :label="$t('settings.vrsSettingsForm.ftpSettings.protocol')">{{ item.defaultValue.userProtocol }}</DescriptionsItem>
					<DescriptionsItem :label="$t('settings.vrsSettingsForm.ftpSettings.host')">{{ item.defaultValue.host }}</DescriptionsItem>
					<DescriptionsItem :label="$t('settings.vrsSettingsForm.ftpSettings.port')">{{ item.defaultValue.port }}</DescriptionsItem>
					<DescriptionsItem :label="$t('settings.vrsSettingsForm.ftpSettings.username')">{{ item.defaultValue.username }}</DescriptionsItem>
					<DescriptionsItem :label="$t('settings.vrsSettingsForm.ftpSettings.password')">{{ item.defaultValue.password }}</DescriptionsItem>
					<DescriptionsItem :label="$t('settings.vrsSettingsForm.ftpSettings.' + item.key)">{{ item.defaultValue.fullFilename }}</DescriptionsItem>
				</Descriptions>
			</div>
		</template>
		<template #footer>
			<template v-if="item != null">
				<div class="flex-wrapper">
					<Button v-if="item.defaultValue != null" key="defaults" @click="onApplyDefaults" :loading="isSubmitting">{{ $t('settings.vrsSettingsForm.applyDefaultsButtonLabel') }}</Button>
					<div class="flex-grow"></div>
					<Button key="back" @click="onCancel" :loading="isSubmitting">{{ $t('settings.vrsSettingsForm.cancelButtonLabel') }}</Button>
					<Button type="primary" @click="onOk" :loading="isSubmitting">{{ $t('settings.vrsSettingsForm.buttonLabel') }}</Button>
				</div>
			</template>
		</template>
	</Modal>
</template>

<script>
import { reactive, ref, computed, watch, toRef, toRaw } from 'vue';
import {
	Modal, Form, FormItem, Button, RadioGroup,
	Descriptions, DescriptionsItem, message,
} from 'ant-design-vue';

import Api from '@/api/Api';
import Validators from '@/api/Validators';
import Utils from '@/api/Utils';

import { useI18n } from 'vue-i18n';

import { showErrorMessage } from '@/components/common/errorMessage';

import InputRestricted from '@/components/common/InputRestricted';
import InputDecimal from '@/components/common/InputDecimal';
import DividerAutoDirection from '@/components/common/DividerAutoDirection.vue';

const get_default_state = () => ({
	key: '',
	userProtocol: 'ftp',
	host: '',
	port: 0,
	username: '',
	password: '',
	fullFilename: '',
})

const get_protocols = () => {
	return ['ftp', 'http'].map(value => ({
		'value': value,
		'label': value,
	}))
}

export default {
	components: {
		Modal, Form, FormItem, InputRestricted, InputDecimal,
		Button, RadioGroup, Descriptions, DescriptionsItem,
		DividerAutoDirection,
	},
	emits: ['update:modelValue'],
	props: ['modelValue', 'onFTPSettingsUpdate', 'item'],
	setup(props, { emit }) {
		const { t } = useI18n();
		const initialFormState = reactive(get_default_state());
		const formState = reactive(get_default_state());
		const { validate, validateInfos } = Validators.useVRSSettingsFtpFormRules(formState);
		const isSubmitting = ref(false);

		const itemRef = toRef(props, 'item');
		const modalTitle = computed(() => {
			if (itemRef.value == null) {
				return '';
			}
			return t('settings.vrsSettingsForm.ftpSettings.headers.' + itemRef.value.key);
		});
		watch(itemRef, value => {
			if (value) {
				Utils.updateObject(initialFormState, value.currentValue);
				Utils.updateObject(formState, initialFormState);
			}
		});

		const modelValueRef = toRef(props, 'modelValue');
		const visible = computed({
			get: () => modelValueRef.value,
			set: () => emit('update:modelValue'),
		});
		watch(modelValueRef, value => {
			if (value) {
				Utils.updateObject(formState, initialFormState);
			}
		});

		const handleResponse = response => {
			if (response.status === 'ok') {
				message.success(t('settings.vrsSettingsForm.messages.success'));
				visible.value = false;
				props.onFTPSettingsUpdate();
			} else {
				showErrorMessage(response.errors);
			}
			isSubmitting.value = false;
		};

		const onOk = () => {
			validate().then(() => {
				isSubmitting.value = true;
				Api.vrsFTPSettingsPost(toRaw(formState), handleResponse);
			}).catch(err => {
				console.log('error', err);
			});
		};

		const onApplyDefaults = () => {
			isSubmitting.value = true;
			Api.vrsFTPSettingsApplyDefaults(formState.key, handleResponse);
		};

		const onCancel = () => {
			visible.value = false;
		};

		return {
			initialFormState,
			formState,
			validateInfos,
			isSubmitting,
			visible,
			modalTitle,

			protocols: get_protocols(),

			onOk,
			onCancel,
			onApplyDefaults,

			hostnameChecker: Validators.hostnameChecker,
			alphaNumericChecker: Validators.alphaNumericChecker,
			filenameChecker: Validators.filenameChecker,
		};
	},
}
</script>
